import type { Place } from '../types/Place';

export interface MonthGroup {
  key: string;           // ISO format "2024-03" for identification
  displayName: string;   // Localized display "Март 2024"
  places: Place[];       // Places in this month, sorted by dateSort
  count: number;         // Number of places
}

/**
 * Groups places by month (extracting YYYY-MM from dateSort).
 * Returns groups in chronological order (oldest first).
 * Places within each group are sorted by dateSort.
 */
export function groupByMonth(places: Place[]): MonthGroup[] {
  // Create map to group by month key
  const groupMap = new Map<string, Place[]>();

  // Group places by YYYY-MM extracted from dateSort
  for (const place of places) {
    // Extract YYYY-MM from dateSort (e.g., "2025-01-14" -> "2025-01")
    const key = place.dateSort.substring(0, 7);
    if (!groupMap.has(key)) {
      groupMap.set(key, []);
    }
    groupMap.get(key)!.push(place);
  }

  // Convert to array and sort each group by dateSort
  const groups: MonthGroup[] = [];

  for (const [key, monthPlaces] of groupMap) {
    // Sort places within month by dateSort (chronological)
    const sortedPlaces = [...monthPlaces].sort((a, b) =>
      a.dateSort.localeCompare(b.dateSort)
    );

    // Get display name from first place's dateDisplay (if set) or date field
    // Defensive check in case of empty group (shouldn't happen but prevents runtime crash)
    const firstPlace = sortedPlaces[0];
    const displayName = firstPlace?.dateDisplay ?? firstPlace?.date ?? '';

    groups.push({
      key,
      displayName,
      places: sortedPlaces,
      count: sortedPlaces.length,
    });
  }

  // Sort groups chronologically (oldest first)
  groups.sort((a, b) => a.key.localeCompare(b.key));

  return groups;
}

/**
 * Gets the plural form for English word "place".
 * 1 place, 2+ places
 */
export function getPlaceCountLabel(count: number): string {
  if (count === 1) {
    return `${count} place`;
  }
  return `${count} places`;
}